import { useState, useRef, useEffect } from "react"
import { Modal } from "react-bootstrap"
import { SensorUserPositions, SosPositionsList } from "../../services/authinterface"

type Sensor = {
  dx: string
  dy: string
  width: string   // original image width from API
  height: string  // original image height from API
  name: string
  sensorUserPositions: SensorUserPositions[] | null
  relatedSensors: SosPositionsList[] | null
}

type Props = {
  show: boolean
  onClose: () => void
  selectedImage: {
    planimage: string
    FloorplanSensor?: Sensor[]
  } | null
}

function ImageModel({ show, onClose, selectedImage }: Props) {
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (containerRef.current) {
      const updateSize = () => {
        if (!containerRef.current) return
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        })
      }

      updateSize()
      const resizeObserver = new ResizeObserver(updateSize)
      resizeObserver.observe(containerRef.current)

      return () => resizeObserver.disconnect()
    }
  }, [selectedImage])

  // Calculate aspect ratio from the first sensor's dimensions
  const getAspectRatio = () => {
    if (!selectedImage?.FloorplanSensor?.length) return 1;

    // Find sensor with the smallest width
    const smallestWidthSensor = selectedImage.FloorplanSensor.reduce((min, sensor) => {
      return parseFloat(sensor.width) < parseFloat(min.width) ? sensor : min;
    });

    const originalWidth = parseFloat(smallestWidthSensor.width);
    const originalHeight = parseFloat(smallestWidthSensor.height);

    return originalWidth / originalHeight;
  };


  const aspectRatio = getAspectRatio()

  return (
    <Modal
      className="comman-modal"
      show={show}
      onHide={onClose}
      centered
    >
      <Modal.Header closeButton className="comman-modal-header" />
      <Modal.Body className="comman-modal-body text-center">
        {selectedImage && selectedImage.planimage ? (
          <div
            ref={containerRef}
            className="position-relative d-inline-block mx-auto"
            style={{
              width: "100%",
              maxWidth: "800px",
              aspectRatio: aspectRatio,
              maxHeight: "80vh"
            }}
          >
            {/* Floorplan Image */}
            <img
              src={selectedImage.planimage}
              alt="Preview"
              className="img-fluid d-block w-100 h-100"
              style={{ objectFit: "contain" }}
            />

            {/* Sensor Icons */}
            {selectedImage.FloorplanSensor?.map((sensor, index) => {
              if (!sensor.dx || !sensor.dy) {
                return null
              }
              const iconWidth = 35
              const iconHeight = 35

              if (!containerSize.width || !containerSize.height) return null

              const originalWidth = parseFloat(sensor.width)
              const originalHeight = parseFloat(sensor.height)

              const dx = parseFloat(sensor.dx)
              const dy = parseFloat(sensor.dy)

              // Calculate position relative to container
              const mappedDx = (dx / originalWidth) * containerSize.width;
              const mappedDy = (dy / originalHeight) * containerSize.height;

              const policeCount = sensor?.sensorUserPositions?.filter(item => item?.is_police)?.length || 0;
              const userCount = sensor?.sensorUserPositions?.filter(item => !item?.is_police)?.length || 0;
              const sosCount = sensor?.relatedSensors?.filter(item => item?.status === "1")?.length || 0;

              return (
                <>
                  <div
                    key={index}
                    style={{
                      position: "absolute",
                      left: `${mappedDx + 10}px`,
                      top: `${mappedDy + 15}px`,
                      width: `${iconWidth}px`,
                      height: `${iconHeight}px`,
                      zIndex: 10,
                      pointerEvents: "auto"
                    }}
                    className="tooltip-custom"
                    data-tooltip={sensor.name || `Sensor ${index + 1}`}

                  >
                    <svg
                      version="1.0"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 800.000000 800.000000"
                      preserveAspectRatio="xMidYMid meet"
                      width="100%"
                      height="100%"
                    >
                      <g transform="translate(0.000000,800.000000) scale(0.100000,-0.100000)"
                        fill="#00FF33" stroke="none">
                        <path d="M3685 7963 c-27 -3 -52 -8 -55 -13 -3 -5 -46 -13 -95 -16 -50 -4 -93
-12 -97 -18 -4 -6 -41 -12 -82 -14 -56 -3 -76 -8 -76 -18 0 -10 -14 -14 -51
-14 -27 0 -48 -4 -45 -9 7 -11 -28 -21 -76 -21 -26 0 -38 -4 -38 -14 0 -10
-13 -16 -37 -18 -24 -2 -37 -8 -35 -16 1 -6 -1 -11 -5 -11 -35 6 -83 -3 -83
-15 0 -8 -16 -15 -41 -19 -23 -3 -45 -11 -50 -18 -4 -7 -18 -14 -30 -16 -13
-2 -27 -9 -31 -16 -7 -12 -13 -13 -50 -16 -10 0 -18 -8 -18 -16 0 -10 -10 -15
-30 -15 -20 0 -30 -5 -30 -15 0 -10 -10 -15 -30 -15 -16 0 -30 -5 -30 -10 0
-6 -8 -14 -17 -17 -10 -4 -24 -13 -31 -20 -7 -7 -22 -13 -33 -13 -10 0 -19 -7
-19 -15 0 -14 -17 -20 -47 -16 -7 1 -13 -5 -13 -14 0 -8 -7 -15 -15 -15 -8 0
-15 -7 -15 -15 0 -8 -9 -15 -20 -15 -11 0 -28 -9 -37 -20 -10 -11 -23 -20 -30
-20 -7 0 -13 -7 -13 -15 0 -9 -9 -15 -24 -15 -13 0 -26 -7 -30 -15 -3 -8 -12
-15 -21 -15 -8 0 -15 -7 -15 -15 0 -8 -7 -15 -15 -15 -8 0 -15 -7 -15 -15 0
-8 -7 -15 -15 -15 -9 0 -18 -7 -21 -15 -4 -8 -10 -15 -15 -15 -8 0 -18 -7 -70
-55 -9 -8 -21 -15 -27 -15 -7 0 -12 -7 -12 -15 0 -8 -7 -15 -15 -15 -8 0 -15
-7 -15 -15 0 -8 -7 -15 -15 -15 -8 0 -15 -7 -15 -15 0 -8 -7 -15 -15 -15 -8 0
-15 -7 -15 -15 0 -8 -7 -15 -15 -15 -8 0 -15 -7 -15 -15 0 -8 -7 -15 -15 -15
-8 0 -15 -7 -15 -15 0 -8 -6 -15 -14 -15 -7 0 -16 -9 -19 -21 -3 -12 -10 -18
-16 -14 -6 3 -11 0 -11 -7 0 -7 -13 -25 -30 -40 -16 -14 -27 -28 -24 -30 2 -3
-7 -17 -21 -32 -14 -15 -25 -31 -25 -37 0 -5 -7 -9 -15 -9 -8 0 -15 -7 -15
-15 0 -8 -7 -15 -15 -15 -8 0 -15 -9 -15 -19 0 -11 -7 -26 -15 -35 -8 -8 -15
-19 -15 -25 0 -6 -7 -11 -15 -11 -8 0 -15 -7 -15 -15 0 -7 -7 -18 -15 -23 -9
-4 -17 -19 -18 -32 -1 -13 -9 -25 -19 -28 -10 -2 -16 -8 -13 -12 2 -4 -4 -18
-15 -30 -11 -13 -20 -29 -20 -37 0 -7 -7 -13 -15 -13 -10 0 -15 -10 -15 -30 0
-20 -5 -30 -15 -30 -13 0 -21 -25 -16 -52 0 -5 -6 -8 -14 -8 -8 0 -15 -9 -15
-19 0 -11 -6 -26 -14 -33 -7 -8 -17 -30 -21 -49 -7 -28 -9 -31 -15 -14 -5 14
-8 8 -13 -24 -3 -24 -11 -48 -19 -52 -13 -8 -14 -12 -17 -50 0 -11 -5 -18 -10
-14 -6 3 -13 -10 -17 -30 -3 -19 -10 -35 -16 -35 -5 0 -7 -4 -3 -9 3 -5 1 -12
-5 -16 -5 -3 -10 -19 -10 -36 0 -19 -5 -29 -15 -29 -11 0 -15 -12 -15 -50 0
-38 -4 -50 -15 -50 -12 0 -15 -13 -15 -55 0 -42 -3 -55 -15 -55 -11 0 -15 -13
-15 -54 0 -35 -5 -60 -15 -70 -8 -8 -15 -28 -15 -44 0 -16 -3 -51 -7 -78 -5
-36 -8 -43 -14 -29 -5 13 -8 -1 -9 -40 -3 -109 -11 -190 -21 -193 -5 -2 -9
-114 -8 -270 0 -147 4 -258 8 -247 8 20 20 -88 22 -189 0 -38 3 -46 9 -31 l9
20 2 -20 c1 -11 3 -24 4 -30 2 -5 3 -34 4 -62 1 -37 5 -52 14 -50 8 1 13 -17
15 -60 2 -47 7 -63 17 -63 11 0 15 -13 15 -50 0 -34 4 -50 13 -50 8 0 14 -15
16 -41 1 -23 8 -48 16 -56 8 -8 15 -30 15 -49 0 -24 5 -34 15 -34 10 0 15 -10
15 -34 0 -41 9 -68 20 -61 4 3 11 -16 15 -42 4 -28 10 -41 14 -34 9 14 21 -18
21 -55 0 -12 7 -24 15 -28 9 -3 15 -18 15 -36 0 -20 5 -30 15 -30 8 0 14 -3
14 -7 -6 -35 3 -73 16 -73 10 0 15 -11 15 -35 0 -24 5 -35 15 -35 10 0 15 -10
15 -30 0 -20 5 -30 15 -30 10 0 15 -10 15 -30 0 -19 5 -30 14 -30 8 0 17 -15
20 -35 4 -20 11 -33 15 -30 5 2 11 -9 15 -26 3 -16 13 -37 21 -45 8 -9 15 -24
15 -35 0 -10 7 -19 15 -19 10 0 15 -10 15 -30 0 -20 5 -30 15 -30 10 0 15 -10
15 -30 0 -20 5 -30 15 -30 8 0 15 -9 15 -19 0 -10 7 -21 15 -25 8 -3 15 -17
15 -31 0 -16 6 -25 15 -25 10 0 15 -10 15 -30 0 -20 5 -30 15 -30 8 0 15 -9
15 -20 0 -11 5 -20 11 -20 5 0 8 -4 5 -9 -3 -5 1 -11 9 -15 8 -3 15 -12 15
-21 0 -8 4 -15 9 -15 5 0 12 -13 16 -30 4 -16 13 -30 21 -30 9 0 14 -11 14
-30 0 -20 5 -30 15 -30 8 0 15 -6 15 -14 0 -8 7 -16 15 -20 8 -3 15 -17 15
-31 0 -16 6 -25 15 -25 8 0 15 -9 15 -20 0 -11 7 -20 15 -20 10 0 15 -10 15
-30 0 -20 5 -30 15 -30 8 0 15 -7 15 -15 0 -8 5 -15 10 -15 6 0 16 -12 23 -27
6 -16 17 -32 24 -36 7 -4 13 -15 13 -22 0 -8 4 -15 10 -15 5 0 12 -11 16 -24
3 -14 12 -27 20 -30 8 -3 14 -12 14 -21 0 -8 8 -15 18 -16 14 0 14 -2 -3 -9
-17 -7 -17 -9 -2 -9 10 -1 17 -9 17 -21 0 -11 7 -20 15 -20 8 0 15 -7 15 -15
0 -8 7 -15 15 -15 10 0 15 -10 15 -30 0 -19 5 -30 14 -30 7 0 16 -9 19 -21 3
-12 10 -18 16 -14 7 3 11 -2 11 -15 0 -11 9 -28 20 -37 11 -10 20 -23 20 -30
0 -7 6 -13 14 -13 8 0 17 -11 20 -25 4 -14 11 -25 16 -25 6 0 10 -9 10 -20 0
-11 7 -20 15 -20 8 0 15 -7 15 -15 0 -8 7 -15 15 -15 8 0 15 -9 15 -19 0 -11
6 -26 13 -33 7 -7 16 -21 20 -30 3 -10 11 -18 17 -18 5 0 10 -7 10 -15 0 -8 6
-15 14 -15 8 0 17 -14 21 -30 4 -17 11 -27 16 -24 6 3 13 -3 16 -15 3 -12 12
-21 19 -21 8 0 14 -6 14 -14 0 -8 7 -16 15 -20 8 -3 15 -18 15 -33 0 -21 3
-24 9 -14 8 12 11 9 16 -12 4 -15 13 -27 21 -27 8 0 14 -9 14 -20 0 -11 7 -20
15 -20 8 0 15 -7 15 -15 0 -8 6 -15 14 -15 8 0 17 -12 20 -26 4 -15 20 -38 37
-53 16 -14 29 -31 29 -39 0 -7 5 -10 11 -7 6 4 13 -2 16 -14 3 -12 12 -21 19
-21 8 0 13 -3 13 -7 -5 -28 3 -53 16 -53 8 0 15 -7 15 -15 0 -8 7 -15 15 -15
8 0 15 -7 15 -15 0 -8 7 -15 15 -15 8 0 15 -7 15 -15 0 -8 6 -15 14 -15 8 0
17 -12 20 -26 4 -15 20 -38 37 -53 16 -14 29 -31 29 -39 0 -7 5 -10 11 -7 6 4
13 -2 16 -14 3 -12 12 -21 19 -21 8 0 14 -6 14 -14 0 -8 7 -16 15 -20 8 -3 15
-17 15 -31 0 -16 6 -25 15 -25 8 0 15 -7 15 -15 0 -8 7 -15 15 -15 8 0 15 -6
15 -14 0 -7 9 -16 20 -19 11 -3 19 -11 18 -19 -2 -7 3 -12 10 -10 6 1 12 -5
12 -13 0 -9 7 -18 15 -21 8 -4 15 -12 15 -20 0 -8 9 -14 20 -14 11 0 19 -3 19
-7 -1 -5 -1 -19 0 -33 1 -19 3 -22 10 -11 8 12 11 9 16 -12 4 -15 13 -27 21
-27 8 0 14 -7 14 -15 0 -8 7 -15 15 -15 8 0 15 -7 15 -15 0 -8 7 -15 15 -15 8
0 15 -9 15 -21 0 -12 4 -17 11 -14 6 4 13 -2 16 -14 3 -12 12 -21 19 -21 8 0
14 -7 14 -15 0 -8 7 -15 15 -15 8 0 15 -7 15 -15 0 -8 6 -15 14 -15 8 0 18
-14 21 -31 5 -22 10 -28 15 -18 6 9 11 5 15 -14 4 -15 13 -27 21 -27 8 0 14
-7 14 -15 0 -8 7 -15 15 -15 8 0 15 -7 15 -15 0 -8 7 -15 15 -15 8 0 15 -7 15
-16 0 -9 9 -18 20 -21 11 -3 17 -10 14 -15 -3 -5 3 -12 14 -15 13 -3 18 -11
15 -19 -3 -9 2 -14 16 -14 12 0 21 -4 21 -10 0 -13 37 -50 50 -50 6 0 10 -7
10 -15 0 -8 7 -15 15 -15 8 0 15 -9 15 -21 0 -12 4 -17 11 -14 6 4 13 -2 16
-14 3 -12 12 -21 19 -21 8 0 14 -7 14 -15 0 -8 7 -15 15 -15 8 0 15 -7 15 -15
0 -8 7 -15 15 -15 8 0 15 7 15 15 0 8 7 15 15 15 8 0 15 7 15 15 0 8 7 15 15
15 8 0 15 7 15 16 0 9 9 18 20 21 11 3 17 10 14 15 -3 5 3 12 15 15 12 3 21
12 21 19 0 8 7 14 15 14 8 0 15 7 15 15 0 8 7 15 15 15 8 0 15 7 15 15 0 8 7
15 15 15 8 0 15 7 15 15 0 8 7 15 15 15 8 0 15 7 15 15 0 8 7 15 15 15 8 0 15
9 15 20 0 11 5 20 10 20 13 0 50 37 50 50 0 6 6 10 14 10 8 0 17 12 21 27 4
19 9 23 15 14 5 -10 10 -4 15 18 3 17 13 31 21 31 8 0 14 7 14 15 0 8 7 15 15
15 8 0 15 7 15 15 0 8 6 15 14 15 7 0 16 9 19 21 3 12 10 18 16 14 6 -3 11 1
11 10 0 8 7 18 15 21 8 4 15 12 15 20 0 8 7 14 15 14 8 0 15 7 15 15 0 8 7 15
15 15 8 0 15 7 15 15 0 8 8 15 18 15 13 0 14 2 4 9 -11 7 -11 9 2 14 9 4 16
13 16 22 0 8 7 15 15 15 8 0 15 7 15 15 0 8 7 15 15 15 8 0 15 7 15 15 0 9 7
18 15 21 8 4 15 12 15 20 0 8 7 14 15 14 8 0 15 7 15 15 0 8 7 15 15 15 8 0
15 7 15 15 0 8 6 15 14 15 8 0 17 13 21 30 4 16 13 30 21 30 8 0 14 7 14 15 0
8 7 15 15 15 8 0 15 6 15 14 0 8 7 16 15 20 8 3 15 12 15 21 0 8 7 15 15 15 8
0 15 7 15 15 0 8 6 15 13 15 7 0 14 10 14 22 1 13 9 28 18 34 8 6 15 16 15 23
0 6 7 11 15 11 8 0 15 7 15 15 0 8 7 15 15 15 8 0 15 7 15 15 0 8 6 15 14 15
8 0 17 14 21 30 4 17 13 30 21 30 8 0 14 9 14 20 0 11 7 20 15 20 8 0 15 7 15
15 0 8 7 15 15 15 8 0 15 7 15 15 0 8 7 15 15 15 10 0 15 10 15 30 0 20 5 30
15 30 8 0 15 7 15 15 0 8 6 15 14 15 7 0 16 9 19 21 3 12 10 18 16 14 6 -3 11
1 11 9 0 8 8 20 18 25 10 6 12 11 4 11 -7 0 1 13 18 30 16 16 30 34 30 40 0 5
7 10 15 10 8 0 15 6 15 14 0 8 7 16 15 20 8 3 15 15 15 26 0 11 7 23 15 26 8
4 15 12 15 20 0 8 7 14 15 14 8 0 15 9 15 20 0 11 6 20 14 20 9 0 16 12 18 28
2 15 8 26 16 25 6 -2 12 4 12 12 0 9 6 18 14 21 8 3 18 19 21 35 4 16 11 26
16 23 6 -3 13 3 16 15 3 12 12 21 19 21 8 0 14 9 14 20 0 11 7 20 15 20 9 0
15 9 15 25 0 14 7 28 15 31 8 4 15 12 15 20 0 8 7 14 15 14 9 0 15 9 15 25 0
14 7 28 15 31 8 4 15 12 15 20 0 8 6 14 14 14 8 0 17 13 21 30 4 16 13 30 21
30 8 0 14 9 14 20 0 11 7 20 16 20 8 0 12 5 9 10 -3 6 1 13 10 16 13 6 14 8 3
15 -10 7 -9 9 5 9 10 0 17 8 17 20 0 11 7 20 15 20 9 0 15 9 15 25 0 14 7 28
15 31 8 4 15 12 15 20 0 8 7 14 15 14 9 0 15 9 15 25 0 14 7 28 15 31 8 4 15
13 15 20 0 8 6 17 14 20 8 3 18 19 21 35 4 16 13 29 21 29 8 0 13 3 13 8 -1 4
-1 16 0 27 1 15 3 17 10 6 7 -10 11 -5 16 18 3 17 13 31 21 31 9 0 14 11 14
30 0 20 5 30 15 30 8 0 15 9 15 20 0 11 7 20 15 20 8 0 14 3 14 8 -5 27 3 52
16 52 10 0 15 10 15 30 0 19 5 30 14 30 8 0 17 14 21 30 4 17 10 30 15 30 5 0
12 14 15 31 5 23 9 28 16 18 7 -11 9 -10 10 4 3 34 4 38 16 45 6 4 15 20 18
35 4 15 11 27 15 27 5 0 11 13 15 30 4 16 13 30 21 30 9 0 14 11 14 30 0 20 5
30 15 30 10 0 15 11 15 35 0 24 5 35 15 35 10 0 15 10 15 30 0 20 5 30 15 30
10 0 15 10 15 30 0 20 5 30 15 30 10 0 15 10 15 30 0 17 6 33 14 36 8 3 17 25
21 49 3 27 11 44 18 42 7 -1 13 12 15 31 2 20 9 32 18 32 9 0 14 10 14 29 0
37 10 62 21 55 5 -3 9 11 9 30 0 20 6 38 14 43 9 5 16 26 17 51 2 24 8 42 15
42 6 0 14 19 17 42 3 24 11 44 16 46 6 2 11 19 11 37 0 19 6 40 13 47 8 7 17
33 21 58 4 24 11 47 15 50 5 3 12 29 15 59 3 30 11 58 18 62 7 4 15 42 18 86
4 43 8 71 9 63 1 -13 2 -13 11 0 5 8 10 56 10 107 0 68 4 95 15 107 13 12 15
56 15 265 0 216 -2 251 -15 251 -12 0 -15 19 -15 109 0 84 -3 111 -15 121 -10
9 -15 32 -15 76 0 44 -4 65 -14 71 -12 7 -22 53 -25 121 -1 7 -5 11 -10 8 -5
-3 -12 20 -16 52 -4 31 -12 60 -19 64 -12 8 -13 11 -15 63 -1 22 -6 34 -13 32
-9 -1 -14 14 -16 46 -2 33 -7 47 -18 47 -9 0 -14 11 -14 35 0 23 -4 34 -12 32
-8 -1 -14 11 -16 31 -2 20 -9 32 -18 32 -9 0 -14 11 -14 30 0 18 -6 33 -15 36
-8 4 -15 17 -15 30 0 14 -6 27 -14 30 -8 3 -18 19 -21 35 -4 16 -10 27 -15 24
-4 -3 -11 11 -14 30 -4 21 -13 35 -21 35 -8 0 -14 3 -14 8 5 27 -3 52 -16 52
-10 0 -15 10 -15 31 0 19 -4 28 -11 24 -6 -4 -13 2 -16 14 -3 12 -12 21 -19
21 -12 0 -19 26 -15 53 0 4 -6 7 -14 7 -8 0 -15 9 -15 20 0 11 -7 20 -15 20
-8 0 -15 7 -15 15 0 8 -6 15 -14 15 -8 0 -17 14 -21 30 -4 17 -13 30 -21 30
-8 0 -14 6 -14 14 0 8 -7 17 -16 20 -8 3 -12 10 -9 16 3 5 -1 10 -10 10 -9 0
-21 13 -26 30 -6 17 -15 30 -20 30 -5 0 -9 7 -9 15 0 8 -6 15 -14 15 -7 0 -16
9 -19 21 -3 12 -10 18 -16 14 -7 -3 -11 2 -11 14 0 12 -7 21 -15 21 -8 0 -15
7 -15 15 0 8 -5 15 -10 15 -6 0 -31 20 -56 45 -24 25 -49 45 -54 45 -6 0 -10
7 -10 15 0 8 -7 15 -15 15 -8 0 -15 7 -15 15 0 8 -7 15 -15 15 -8 0 -15 7 -15
15 0 8 -6 15 -13 15 -7 0 -20 9 -30 20 -9 11 -24 20 -32 20 -8 0 -15 7 -15 15
0 8 -9 15 -20 15 -11 0 -20 7 -20 15 0 8 -7 15 -15 15 -8 0 -15 7 -15 15 0 8
-7 15 -15 15 -8 0 -15 7 -15 15 0 14 -17 20 -47 16 -7 -1 -13 5 -13 12 0 8 -9
17 -21 20 -12 3 -18 10 -15 16 3 5 -7 12 -23 16 -16 3 -32 13 -35 21 -3 8 -12
14 -21 14 -8 0 -15 7 -15 15 0 10 -10 15 -30 15 -20 0 -30 5 -30 15 0 9 -9 15
-25 15 -15 0 -25 6 -25 14 0 8 -10 16 -22 18 -13 2 -22 9 -20 16 1 7 -9 12
-27 12 -16 0 -31 6 -35 15 -3 8 -17 15 -30 15 -14 0 -29 7 -33 15 -5 8 -19 16
-32 18 -12 2 -26 9 -30 15 -4 7 -27 16 -52 21 -24 5 -37 9 -29 10 29 2 -13 21
-47 21 -20 0 -32 5 -30 12 1 7 -15 15 -38 19 -21 3 -41 12 -43 18 -2 6 -22 11
-44 11 -32 0 -39 3 -34 15 4 12 -5 15 -49 15 -37 0 -57 5 -62 14 -5 9 -28 16
-58 18 -32 3 -49 8 -48 16 2 8 -13 12 -46 12 -60 0 -112 10 -106 20 3 4 -40
11 -95 15 -55 4 -98 10 -95 15 10 16 -545 26 -720 13z m539 -978 c4 -12 25
-15 95 -15 49 0 97 -3 107 -7 16 -6 16 -7 -2 -14 -12 -5 7 -8 48 -8 43 -1 72
-6 81 -15 7 -8 34 -16 58 -18 24 -3 43 -9 42 -15 -1 -6 19 -13 45 -16 25 -3
48 -10 50 -16 2 -6 19 -13 38 -17 18 -3 31 -10 29 -15 -3 -4 10 -11 30 -15 20
-3 35 -12 35 -20 0 -9 11 -14 30 -14 20 0 30 -5 30 -15 0 -10 10 -15 30 -15
20 0 30 -5 30 -15 0 -10 10 -15 30 -15 20 0 30 -5 30 -15 0 -8 9 -15 20 -15
11 0 20 -6 20 -14 0 -8 14 -17 30 -21 17 -4 30 -11 30 -16 0 -5 7 -9 15 -9 8
0 15 -7 15 -15 0 -8 6 -15 13 -15 7 0 23 -9 35 -20 12 -11 26 -20 32 -20 5 0
10 -7 10 -15 0 -8 9 -15 20 -15 11 0 20 -7 20 -15 0 -8 7 -15 15 -15 8 0 15
-7 15 -15 0 -8 7 -15 15 -15 8 0 15 -7 15 -15 0 -8 7 -15 15 -15 8 0 15 -7 15
-15 0 -8 7 -15 15 -15 8 0 15 -7 15 -15 0 -8 6 -15 14 -15 7 0 16 -9 19 -21 3
-12 10 -18 16 -14 6 3 11 1 11 -6 0 -7 21 -33 48 -57 26 -24 48 -48 48 -53 1
-5 2 -16 3 -24 0 -8 8 -15 16 -15 8 0 15 -7 15 -15 0 -8 7 -15 15 -15 8 0 15
-9 15 -20 0 -11 7 -20 15 -20 10 0 15 -10 15 -30 0 -19 5 -30 14 -30 12 0 30
-34 27 -52 -1 -5 3 -8 9 -8 5 0 10 -7 10 -15 0 -8 7 -15 15 -15 13 0 18 -18
14 -47 0 -3 6 -10 14 -14 8 -5 18 -21 21 -35 4 -15 14 -33 21 -41 8 -8 15 -27
15 -43 0 -16 7 -30 15 -34 9 -3 15 -19 15 -41 0 -23 4 -34 13 -33 8 2 13 -13
15 -44 2 -34 7 -48 18 -48 10 0 14 -13 14 -51 0 -27 4 -48 9 -45 14 9 21 -20
21 -84 0 -45 4 -61 17 -68 15 -9 17 -41 17 -345 -1 -300 -2 -337 -17 -352 -12
-12 -17 -34 -17 -81 0 -50 -3 -64 -15 -64 -11 0 -15 -12 -15 -50 0 -38 -4 -50
-15 -50 -8 0 -14 -3 -14 -7 1 -5 1 -25 0 -45 -1 -27 -6 -38 -16 -38 -11 0 -15
-12 -15 -45 0 -33 -4 -45 -15 -45 -10 0 -15 -10 -15 -30 0 -16 -7 -33 -15 -37
-8 -5 -16 -19 -18 -32 -2 -12 -9 -26 -15 -30 -6 -4 -13 -18 -14 -31 -2 -14
-10 -28 -18 -31 -8 -3 -16 -11 -16 -18 -1 -6 -2 -18 -3 -26 0 -8 -8 -15 -16
-15 -8 0 -15 -7 -15 -16 0 -8 -7 -22 -15 -30 -8 -9 -15 -24 -15 -35 0 -10 -7
-19 -15 -19 -8 0 -15 -7 -15 -15 0 -8 -7 -15 -15 -15 -10 0 -15 -10 -15 -30 0
-20 -5 -30 -15 -30 -8 0 -15 -7 -15 -15 0 -8 -7 -15 -15 -15 -8 0 -15 -5 -15
-12 0 -6 -20 -31 -44 -54 -25 -23 -43 -44 -40 -47 2 -2 -7 -16 -21 -31 -14
-15 -25 -31 -25 -37 0 -5 -6 -9 -12 -9 -7 0 -24 -12 -37 -25 -13 -14 -26 -24
-28 -21 -3 2 -24 -15 -47 -40 -23 -24 -48 -44 -54 -44 -7 0 -12 -7 -12 -15 0
-8 -5 -15 -11 -15 -6 0 -17 -7 -25 -15 -9 -8 -24 -15 -35 -15 -10 0 -19 -7
-19 -15 0 -8 -6 -15 -14 -15 -8 0 -16 -7 -20 -15 -3 -8 -17 -15 -31 -15 -16 0
-25 -6 -25 -15 0 -8 -9 -15 -20 -15 -11 0 -20 -7 -20 -15 0 -10 -10 -15 -30
-15 -19 0 -30 -5 -30 -14 0 -8 -13 -17 -30 -21 -16 -4 -30 -10 -30 -15 0 -4
-14 -10 -31 -14 -16 -3 -37 -13 -45 -21 -9 -8 -28 -15 -44 -15 -16 0 -30 -7
-34 -15 -3 -9 -18 -15 -35 -15 -16 0 -31 -6 -34 -13 -3 -7 -24 -14 -47 -15
-23 -2 -49 -9 -57 -17 -9 -9 -34 -15 -63 -15 -33 0 -50 -5 -54 -15 -4 -11 -23
-15 -80 -15 -53 0 -79 -4 -90 -15 -12 -13 -57 -15 -279 -15 -202 0 -266 3
-269 12 -3 8 -33 14 -87 16 -59 3 -84 8 -88 18 -3 10 -22 14 -59 14 -41 0 -54
4 -54 15 0 8 -3 14 -7 14 -5 -1 -29 -1 -55 0 -36 1 -48 5 -48 16 0 10 -11 15
-35 15 -23 0 -35 5 -35 14 0 8 -16 15 -41 19 -23 3 -45 10 -48 15 -3 6 -18 13
-33 17 -15 4 -29 13 -32 21 -3 8 -17 14 -31 14 -14 0 -25 5 -25 11 0 5 -13 15
-30 20 -16 6 -30 15 -30 20 0 5 -9 9 -20 9 -11 0 -20 7 -20 15 0 10 -10 15
-30 15 -20 0 -30 5 -30 15 0 8 -7 15 -15 15 -9 0 -18 7 -21 15 -4 8 -12 15
-20 15 -8 0 -14 7 -14 15 0 9 -9 15 -23 15 -13 0 -33 13 -47 30 -14 16 -31 29
-37 30 -7 0 -13 7 -13 15 0 8 -7 15 -15 15 -9 0 -18 7 -21 15 -3 8 -14 17 -25
20 -11 4 -17 10 -14 15 3 5 -4 11 -15 15 -11 4 -18 10 -15 15 3 4 -2 10 -10
14 -8 3 -15 13 -15 23 0 15 -2 15 -9 4 -8 -12 -11 -9 -16 12 -4 15 -13 27 -21
27 -8 0 -14 7 -14 15 0 8 -7 15 -15 15 -8 0 -15 7 -15 15 0 8 -7 15 -15 15 -8
0 -15 9 -15 20 0 11 -7 20 -15 20 -8 0 -15 7 -15 15 0 8 -6 15 -14 15 -8 0
-17 13 -21 30 -4 16 -13 30 -21 30 -8 0 -14 6 -14 14 0 8 -7 16 -15 20 -8 3
-15 16 -15 30 0 13 -7 26 -15 30 -8 3 -15 12 -15 21 0 8 -7 15 -15 15 -10 0
-15 10 -15 30 0 20 -5 30 -15 30 -10 0 -15 10 -15 30 0 20 -5 30 -15 30 -10 0
-15 10 -15 30 0 20 -5 30 -15 30 -8 0 -16 9 -16 20 -3 37 -4 41 -17 49 -8 4
-16 27 -19 50 -3 22 -11 41 -17 41 -6 0 -12 19 -14 42 -2 24 -9 46 -17 51 -10
5 -15 25 -15 58 0 28 -4 48 -9 45 -12 -8 -21 29 -21 90 0 37 -4 54 -15 58 -13
5 -15 53 -15 347 0 299 2 340 15 335 12 -4 15 6 16 57 0 58 15 122 20 87 1 -8
5 11 9 44 4 32 12 61 17 65 6 3 13 26 16 50 3 24 10 47 15 50 6 3 14 19 17 34
4 15 12 27 19 27 7 0 6 4 -2 9 -11 7 -11 9 2 14 10 4 16 18 16 37 0 20 5 30
15 30 10 0 15 10 15 29 0 16 6 31 14 34 7 3 16 19 19 37 4 17 11 29 17 25 5
-3 10 3 10 14 0 12 7 21 15 21 10 0 15 10 15 30 0 20 5 30 15 30 8 0 15 7 15
15 0 7 7 18 15 22 8 5 16 20 19 33 2 14 9 24 15 23 6 -2 11 4 11 12 0 9 7 18
15 21 8 4 15 12 15 20 0 8 9 22 20 31 11 10 20 23 20 30 0 7 7 13 15 13 8 0
15 7 15 15 0 8 6 15 14 15 7 0 16 9 19 21 3 12 10 18 16 14 7 -3 11 2 11 14 0
12 5 21 10 21 6 0 30 20 53 45 24 25 49 45 55 45 7 0 12 7 12 15 0 8 7 15 15
15 8 0 15 7 15 15 0 8 7 15 15 15 8 0 15 6 15 14 0 7 14 16 30 20 18 3 30 12
30 21 0 8 7 15 15 15 8 0 15 5 15 10 0 6 8 14 18 17 9 4 23 13 30 20 7 7 22
13 33 13 10 0 19 7 19 15 0 8 7 15 15 15 8 0 15 6 15 13 0 7 12 14 27 15 15 1
31 9 36 17 4 8 19 15 32 15 16 0 25 6 25 15 0 10 10 15 26 15 15 0 32 7 39 16
6 9 24 17 38 17 15 0 27 5 27 12 0 7 19 16 47 20 28 4 41 10 34 14 -14 9 18
21 56 21 12 0 23 5 25 11 2 5 24 13 48 16 25 4 51 12 58 20 8 7 35 13 63 13
32 0 49 4 49 13 0 8 4 7 10 -3 8 -13 10 -12 10 3 0 15 11 17 94 17 71 0 97 4
106 15 10 12 46 15 205 15 162 0 194 -2 199 -15z"/>
                      </g>
                    </svg>
                  </div>
                  {sosCount && sosCount > 0 ? (
                    <div
                      key={index}
                      style={{
                        position: "absolute",
                        left: `${mappedDx - (window.innerWidth < 600 ? 35 : 25)}px`,
                        top: `${mappedDy + 15}px`,
                        width: `${iconWidth}px`,
                        height: `${iconHeight}px`,
                        zIndex: 10,
                        pointerEvents: "auto",
                      }}
                    >
                      <svg width="30" height="30" viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="50" cy="50" r="50" fill="red" />
                        <text
                          x="50"
                          y="45"
                          textAnchor="middle"
                          dominant-baseline="central"
                          fontSize="50"
                          // fontWeight="bold"
                          fill="white"
                        >
                          {sosCount}
                        </text>
                      </svg>
                    </div>
                  ) : ""}
                  {policeCount && policeCount > 0 ? <div
                    key={index}
                    style={{
                      position: "absolute",
                      left: `${mappedDx + 10}px`,
                      top: `${mappedDy - (window.innerWidth < 600 ? 35 : 25)}px`,
                      width: `${iconWidth}px`,
                      height: `${iconHeight}px`,
                      zIndex: 10,
                      pointerEvents: "auto",
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 120" width="30" height="30">
                      <path d="M50 0 L95 20 V60 C95 90 75 110 50 120 C25 110 5 90 5 60 V20 Z" fill="#00AEEF" stroke="black" stroke-width="1" />
                      <text
                        x="50"
                        y="50"
                        textAnchor="middle"
                        dominant-baseline="central"
                        fontSize="50"
                        // fontWeight="bold"
                        fill="white"
                      >
                        {policeCount}
                      </text>
                    </svg>
                  </div> : ""}
                  {userCount && userCount > 0 ? <div
                    key={index}
                    style={{
                      position: "absolute",
                      left: `${mappedDx + (window.innerWidth < 600 ? 40 : 50)}px`,
                      top: `${mappedDy + 15}px`,
                      width: `${iconWidth}px`,
                      height: `${iconHeight}px`,
                      zIndex: 10,
                      pointerEvents: "auto",
                    }}

                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="30" height="30">
                      <polygon points="50,0 100,50 50,100 0,50" fill="green" stroke="black" stroke-width="1" />
                      <text
                        x="50"
                        y="50"
                        textAnchor="middle"
                        fontSize="40"
                        dominant-baseline="central"
                        // fontWeight="bold"
                        fill="white"
                      >
                        {userCount}
                      </text>
                    </svg>
                  </div> : ""}
                </>
              )
            })}
          </div>
        ) : (
          <p>No image available</p>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default ImageModel